import { useState } from "react";
import { Send, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const X_MONEY_URL = "https://www.x.com/DesiresAHX";

const inputClass =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-white/40";

const STEPS = [
  { index: "01", text: "Send $1 on X Money to @DesiresAHX — every $1 is one entry." },
  { index: "02", text: "Leave your name, X handle and email below so we can match your payment." },
  { index: "03", text: "You're in the draw for $350 in services and a private stay in Las Vegas." },
];

export const Raffle = () => {
  const [form, setForm] = useState({ name: "", x_handle: "", email: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/raffle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Entry received — check your inbox for a confirmation email.");
      setForm({ name: "", x_handle: "", email: "" });
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="raffle"
      data-testid="raffle-section"
      className="relative scroll-mt-28 border-t border-white/10 py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <div className="text-center">
            <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.35em] text-zinc-400">
              <Trophy className="h-4 w-4" /> Thanksgiving raffle
            </p>
            <h2 className="mx-auto mt-4 max-w-3xl font-display text-2xl font-bold uppercase tracking-tight sm:text-4xl lg:text-5xl">
              Win $350 in services <span className="text-zinc-500">+ a private stay in Las Vegas.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
              Entries are already rolling in. One dollar on X Money is all it takes.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 flex justify-center">
            <video
              data-testid="raffle-video"
              src="/files/DAH_Thanksgiving_Raffle_Promo.mp4"
              className="w-full max-w-[340px] rounded-3xl border border-white/15 bg-black sm:max-w-[380px]"
              autoPlay
              muted
              loop
              playsInline
              controls
            />
          </div>
        </Reveal>

        <div className="mx-auto mt-20 max-w-3xl">
          <Reveal>
            <p className="text-center font-mono text-xs uppercase tracking-[0.35em] text-zinc-400">
              How to enter
            </p>
          </Reveal>
          <div className="mt-8 divide-y divide-white/5 border-y border-white/10">
            {STEPS.map((step, i) => (
              <Reveal key={step.index} delay={i * 0.08}>
                <div
                  data-testid={`raffle-step-${step.index}`}
                  className="flex items-baseline gap-6 py-5 sm:gap-10"
                >
                  <span className="shrink-0 font-mono text-xs tracking-[0.3em] text-zinc-500">
                    {step.index}
                  </span>
                  <p className="text-sm leading-relaxed text-zinc-300 sm:text-base">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <form
              onSubmit={handleSubmit}
              data-testid="raffle-form"
              className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-8"
            >
              <h3 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
                Enter the raffle
              </h3>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  data-testid="raffle-name-input"
                  className={inputClass}
                />
                <input
                  required
                  value={form.x_handle}
                  onChange={update("x_handle")}
                  placeholder="Your X handle (@username)"
                  data-testid="raffle-handle-input"
                  className={inputClass}
                />
              </div>
              <input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="Your email"
                data-testid="raffle-email-input"
                className={`mt-4 ${inputClass}`}
              />
              <div className="mt-6 flex flex-wrap items-center gap-5">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="raffle-submit-button"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-black transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-zinc-300 disabled:opacity-50"
                >
                  {sending ? "Entering…" : "Enter Raffle"} <Send className="h-4 w-4" />
                </button>
                <a
                  href={X_MONEY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="raffle-xmoney-link"
                  className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 underline decoration-white/20 underline-offset-8 transition-colors duration-300 hover:text-white"
                >
                  Send $1 on X Money
                </a>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
